import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

// Initialize the Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase config error: missing env vars.');
  throw new Error('Missing environment variables: VITE_SUPABASE_URL and/or VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey
);

// Sessions
export async function createSession(context: string, topic: string, notifyEmail?: string) {
  try {
    const { data, error } = await supabase
      .from('sessions')
      .insert([
        { context, topic, notify_email: notifyEmail }
      ])
      .select('session_id')
      .single();

    if (error) {
      console.error('Error creating session:', error);
      throw error;
    }

    // Increment the mediation count
    await incrementMediationCount();

    return data;
  } catch (error) {
    console.error('Error in createSession:', error);
    throw error;
  }
}

export async function getSessionById(sessionId: string) {
  try {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .eq('session_id', sessionId)
      .single();

    if (error) {
      console.error('Error fetching session:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in getSessionById:', error);
    throw error;
  }
}

export async function updateSynthesis(sessionId: string, synthesisText: string) {
  try {
    const { error } = await supabase
      .from('sessions')
      .update({ synthesis_text: synthesisText })
      .eq('session_id', sessionId);

    if (error) {
      console.error('Error updating synthesis:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in updateSynthesis:', error);
    throw error;
  }
}

export async function getSynthesis(sessionId: string) {
  try {
    const { data, error } = await supabase
      .from('sessions')
      .select('synthesis_text')
      .eq('session_id', sessionId)
      .single();

    if (error) {
      console.error('Error fetching synthesis:', error);
      throw error;
    }

    return data.synthesis_text;
  } catch (error) {
    console.error('Error in getSynthesis:', error);
    throw error;
  }
}

// Messages
export async function addMessageToSession(
  sessionId: string,
  userOrder: 1 | 2,
  textContent?: string,
  audioFile?: string
) {
  try {
    const { data, error } = await supabase
      .from('messages')
      .insert([
        {
          session_id: sessionId,
          user_order: userOrder,
          text_content: textContent,
          audio_file: audioFile
        }
      ])
      .select('message_id')
      .single();

    if (error) {
      console.error('Error creating message:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in addMessageToSession:', error);
    throw error;
  }
}

export async function getMessagesBySessionId(sessionId: string) {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in getMessagesBySessionId:', error);
    throw error;
  }
}

// Stats
export async function getMediationStats() {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('Supabase configuration missing');
    return { mediation_count: 0 };
  }

  try {
    const { data, error } = await supabase
      .from('stats')
      .select('mediation_count')
      .order('last_updated', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No stats record found, return default
        return { mediation_count: 0 };
      }
      console.error('Error fetching mediation count:', error);
      // Return default value instead of throwing
      return { mediation_count: 0 };
    }

    return data;
  } catch (error) {
    console.error('Error in getMediationStats:', error);
    // Return default value instead of throwing
    return { mediation_count: 0 };
  }
}

export async function incrementMediationCount() {
  try {
    // First get the current stats
    const { data: currentStats, error: fetchError } = await supabase
      .from('stats')
      .select('id, mediation_count')
      .order('last_updated', { ascending: false })
      .limit(1)
      .single();

    if (fetchError) {
      if (fetchError.code === 'PGRST116') {
        // Create initial record if none exists
        const { error: insertError } = await supabase
          .from('stats')
          .insert([{
            mediation_count: 1,
            last_updated: new Date().toISOString()
          }])
          .single();

        if (insertError) {
          console.error('Error creating initial stats:', insertError);
          throw insertError;
        }
        return;
      }
      console.error('Error fetching current stats:', fetchError);
      throw fetchError;
    }

    // Then update the count
    const { error: updateError } = await supabase
      .from('stats')
      .update({
        mediation_count: (currentStats?.mediation_count || 0) + 1,
        last_updated: new Date().toISOString()
      })
      .eq('id', currentStats.id)
      .select()
      .single();

    if (updateError) {
      console.error('Error updating mediation count:', updateError);
      throw updateError;
    }
  } catch (error) {
    console.error('Error in incrementMediationCount:', error);
    throw error;
  }
}